import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import WhatsAppButton from './WhatsAppButton';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Eye,
  Star,
  TrendingUp,
  Calendar,
  Users,
  Target,
  CheckCircle,
  MessageSquare,
  Mail,
  Send,
  Upload,
  FileText,
  X
} from 'lucide-react';
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const AgenzieCompatibili = ({ candidatoId, onRichiestaColloquio, onScopriDiPiu }) => {
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCVModal, setShowCVModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [existingColloqui, setExistingColloqui] = useState([]);
  const [cvInviati, setCvInviati] = useState([]); // Track dei CV già inviati

  useEffect(() => {
    if (candidatoId) {
      loadExistingColloqui();
      loadMatches();
      loadStats();
      loadCVInviati();
    }
  }, [candidatoId]);

  const loadCVInviati = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v1/candidato/colloqui`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filtra solo i colloqui dove il CV è stato inviato (cv_candidato_id presente)
        // Converti in numeri per confronto
        const agenzieConCV = Array.isArray(data) 
          ? data.filter(c => c.cv_candidato_id).map(c => Number(c.agenzia?.id)).filter(Boolean)
          : [];
        setCvInviati(agenzieConCV);
      }
    } catch (error) {
      console.error('Errore nel caricamento CV inviati:', error);
      setCvInviati([]);
    }
  };

  const loadExistingColloqui = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v1/candidato/colloqui`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Estrai gli ID delle agenzie con cui c'è già una richiesta
        // Converti in numeri per confronto consistente
        const agenzieIds = Array.isArray(data) ? data.map(c => Number(c.agenzia?.id)).filter(Boolean) : [];
        console.log('AgenzieCompatibili - Agenzie con colloqui esistenti:', agenzieIds);
        setExistingColloqui(agenzieIds);
      } else {
        console.warn('Errore nel caricamento colloqui, procedo senza filtro');
        setExistingColloqui([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento colloqui esistenti:', error);
      // In caso di errore, imposta array vuoto per non bloccare il filtro
      setExistingColloqui([]);
    }
  };

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/v1/matches?candidate_id=${candidatoId}`);
      if (response.ok) {
        const data = await response.json();
        setMatches(data || []);
      } else {
        console.error('Errore nel caricamento matches:', response.status);
        setMatches([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento matches:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/api/v1/matches/stats?candidate_id=${candidatoId}`);
      if (response.ok) {
        const data = await response.json();
        setStats(data || {});
      } else {
        console.error('Errore nel caricamento stats:', response.status);
      }
    } catch (error) {
      console.error('Errore nel caricamento stats:', error);
    }
  };

  const getAffinitaColor = (affinita) => {
    if (affinita >= 100) return "text-emerald-600";
    if (affinita >= 75) return "text-green-600";
    if (affinita >= 50) return "text-yellow-600";
    if (affinita >= 25) return "text-orange-600";
    return "text-red-600";
  };

  const getAffinitaBadge = (affinita) => {
    if (affinita >= 100) return { color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: "🎯", text: "Perfetto" };
    if (affinita >= 75) return { color: "bg-green-100 text-green-800 border-green-200", icon: "🟢", text: "Alta" };
    if (affinita >= 50) return { color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: "🟡", text: "Media" };
    if (affinita >= 25) return { color: "bg-orange-100 text-orange-800 border-orange-200", icon: "🟠", text: "Bassa" };
    return { color: "bg-red-100 text-red-800 border-red-200", icon: "🔴", text: "Scarsa" };
  };

  const getStatusInfo = (match) => {
    const statusMap = {
      'match_geografico': { 
        color: 'bg-blue-100 text-blue-800 border-blue-200', 
        text: 'Match Geografico',
        description: 'Compatibilità basata sulla vicinanza geografica'
      },
      'in_attesa_contatto': { 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
        text: 'In attesa contatto',
        description: 'Hai manifestato interesse, l\'agenzia ti contatterà presto'
      },
      'chiamato': { 
        color: 'bg-green-100 text-green-800 border-green-200', 
        text: 'Contattato',
        description: 'L\'agenzia ti ha contattato'
      },
      'colloquio_fissato': { 
        color: 'bg-purple-100 text-purple-800 border-purple-200', 
        text: 'Colloquio fissato',
        description: 'Hai un colloquio programmato con questa agenzia'
      },
      'inserito': { 
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200', 
        text: 'Inserito',
        description: 'Congratulazioni! Sei stato selezionato per questa posizione'
      },
      'non_interessato': { 
        color: 'bg-red-100 text-red-800 border-red-200', 
        text: 'Non interessato',
        description: 'Hai indicato di non essere interessato'
      }
    };
    
    return statusMap[match.status] || { 
      color: 'bg-gray-100 text-gray-800 border-gray-200', 
      text: match.status,
      description: ''
    };
  };

  const handleRichiestaColloquioClick = (match) => {
    if (onRichiestaColloquio) {
      onRichiestaColloquio({
        id: match.id,
        nome_agenzia: match.agency_name,
        telefono: match.agency_phone,
        citta: match.agency_city,
        affinita_geografica: match.affinita_geografica
      });
    }
  };

  const handleScopriDiPiuClick = (match) => {
    if (onScopriDiPiu) {
      onScopriDiPiu({
        id: match.id,
        nome_agenzia: match.agency_name,
        telefono: match.agency_phone,
        citta: match.agency_city,
        affinita_geografica: match.affinita_geografica,
        note_colloquio: match.note_colloquio,
        messaggio: match.messaggio
      });
    }
  };

  const handleContattoInteresse = async (match) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Devi essere loggato');
        return;
      }

      // Proponi una data colloquio (domani alle 10:00)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dataColloquio = tomorrow.toISOString().split('T')[0];
      const oraColloquio = '10:00:00';

      // Crea una richiesta di colloquio
      const response = await fetch(`${API}/api/v1/richieste-colloquio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agenzia_id: match.agenzia_id,
          candidato_id: candidatoId,
          data_colloquio: dataColloquio,
          ora_colloquio: oraColloquio,
          messaggio: `Richiesta di colloquio per la posizione presso ${match.agency_name}`
        })
      });

      // Parse della risposta PRIMA del controllo ok/error
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Richiesta di colloquio inviata a ${match.agency_name}!`);
        // Aggiungi immediatamente l'agenzia alla lista (per UI reattiva)
        setExistingColloqui(prev => [...prev, match.agency_id]);
        await loadExistingColloqui(); // Ricarica i colloqui esistenti
        await loadMatches(); // Ricarica i dati
      } else {
        // Mostra il messaggio di errore dal backend
        toast.error(data.detail || 'Errore nell\'invio della richiesta');
      }
    } catch (error) {
      console.error('Errore nell\'invio richiesta:', error);
      toast.error('Errore di connessione');
    }
  };

  const handleInviaCV = async (match, cvFile = null) => {
    setShowCVModal(false);
    setCvFile(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Devi essere loggato per inviare il CV');
        return;
      }

      toast.loading('Invio CV in corso...', { id: 'cv-sending' });
      
      // Prepara i dati per l'invio
      let requestOptions;
      let endpoint;
      
      if (cvFile) {
        // Invio con file caricato - usa endpoint dedicato
        const formData = new FormData();
        formData.append('cv_file', cvFile);
        
        endpoint = `${API}/api/v1/cv/invia-cv-file/${match.agenzia_id}`;
        requestOptions = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        };
      } else {
        // Invio CV generato dal profilo - usa endpoint JSON
        endpoint = `${API}/api/v1/cv/invia-cv/${match.agenzia_id}`;
        requestOptions = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        };
      }
      
      const response = await fetch(endpoint, requestOptions);

      if (response.ok) {
        const data = await response.json();
        toast.success(`CV inviato con successo a ${data.agenzia_nome}!`, { id: 'cv-sending' });
        // Aggiungi l'agenzia alla lista dei CV inviati
        setCvInviati(prev => [...prev, match.agency_id]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'invio del CV', { id: 'cv-sending' });
      }
    } catch (error) {
      console.error('Errore nell\'invio CV:', error);
      toast.error('Errore nell\'invio del CV. Riprova più tardi.', { id: 'cv-sending' });
    }
  };

  const handleOpenCVModal = (match) => {
    setSelectedMatch(match);
    setShowCVModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Verifica dimensione (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Il file è troppo grande. Dimensione massima: 5MB');
        return;
      }
      // Verifica tipo
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Formato non supportato. Usa PDF, DOC o DOCX');
        return;
      }
      setCvFile(file);
    }
  };

  const handleSendCV = () => {
    if (selectedMatch) {
      handleInviaCV(selectedMatch, cvFile);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Caricamento agenzie compatibili...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con Statistiche */}
      {stats.total_agencies && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 mx-auto mb-2 opacity-90" />
              <div className="text-2xl font-bold">{stats.compatible_agencies || 0}</div>
              <div className="text-sm opacity-90">Agenzie Compatibili</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-2 opacity-90" />
              <div className="text-2xl font-bold">{stats.perfect_match || 0}</div>
              <div className="text-sm opacity-90">Match Perfetti</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <Star className="w-6 h-6 mx-auto mb-2 opacity-90" />
              <div className="text-2xl font-bold">{stats.high_affinity || 0}</div>
              <div className="text-sm opacity-90">Alta Affinità</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 opacity-90" />
              <div className="text-2xl font-bold">{stats.explicit_matches || 0}</div>
              <div className="text-sm opacity-90">Match Attivi</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista Agenzie */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Agenzie Compatibili ({matches.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {matches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Nessuna agenzia compatibile trovata</h3>
              <p className="text-sm">Aggiorna il tuo profilo per migliorare i match geografici</p>
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                const filteredMatches = matches.filter(match => {
                  // Filtra le agenzie con cui c'è già una richiesta di colloquio
                  const isFiltered = !existingColloqui.includes(match.agency_id);
                  if (!isFiltered) {
                    console.log('Match filtrato (già richiesto):', match.agency_name, match.agency_id);
                  }
                  return isFiltered;
                });
                
                console.log('Matches totali:', matches.length, 'Dopo filtro:', filteredMatches.length);
                
                // Se non ci sono agenzie dopo il filtro, mostra messaggio
                if (filteredMatches.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50 text-green-500" />
                      <h3 className="text-lg font-semibold mb-2">Hai già richiesto colloqui con tutte le agenzie compatibili!</h3>
                      <p className="text-sm">Controlla la sezione "Colloqui" per vedere le tue richieste attive.</p>
                    </div>
                  );
                }
                
                return filteredMatches.map((match, index) => {
                const affinitaBadge = getAffinitaBadge(match.affinita_geografica);
                const statusInfo = getStatusInfo(match);
                const isGeographicMatch = match.match_type === 'geografico';
                // Converti l'ID in numero per confronto consistente
                const agenziaId = Number(match.agency_id);
                const haColloquio = existingColloqui.includes(agenziaId);
                const haCVInviato = cvInviati.includes(agenziaId);
                
                console.log(`AgenzieCompatibili - Agenzia ${match.agency_name} (ID: ${agenziaId}) - Colloquio: ${haColloquio}, CV: ${haCVInviato}`);

                return (
                  <div key={match.id || index} className="bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-all duration-200 border border-gray-200">
                    <div className="flex flex-col lg:flex-row gap-4">
                      {/* Info Agenzia */}
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {match.agency_name?.charAt(0) || 'A'}
                        </div>
                        
                        <div className="flex-1 space-y-3">
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-semibold text-lg">{match.agency_name}</h3>
                              <Badge className={statusInfo.color}>
                                {statusInfo.text}
                              </Badge>
                              <Badge className={`${affinitaBadge.color} border`}>
                                {affinitaBadge.icon} {match.affinita_geografica}% {affinitaBadge.text}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span>{match.agency_city}</span>
                            </div>
                          </div>

                          {/* Progress Bar Affinità */}
                          <div className="space-y-1">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-500">Affinità Geografica</span>
                              <span className={`font-bold ${getAffinitaColor(match.affinita_geografica)}`}>
                                {match.affinita_geografica}%
                              </span>
                            </div>
                            <Progress 
                              value={match.affinita_geografica} 
                              className="h-2"
                            />
                          </div>

                          {/* Descrizione Match */}
                          {match.note_colloquio && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-700">
                                  {match.note_colloquio}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Status Description */}
                          {statusInfo.description && (
                            <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded-lg">
                              {statusInfo.description}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Azioni */}
                      <div className="flex flex-col gap-2 lg:w-48">
                        {/* WhatsApp Contact */}
                        {match.agency_phone && (
                          <WhatsAppButton
                            telefono={match.agency_phone}
                            nome={match.agency_name}
                            tipo="agenzia"
                            className="w-full rounded-xl text-sm"
                          />
                        )}

                        {/* Azioni basate sullo stato */}
                        {isGeographicMatch && match.status === 'match_geografico' && (
                          <>
                            <div className="relative group">
                              <Button
                                onClick={() => !haColloquio && handleContattoInteresse(match)}
                                disabled={haColloquio}
                                className={`w-full rounded-xl text-sm ${
                                  haColloquio 
                                    ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                }`}
                                title={haColloquio ? 'Hai già una richiesta di colloquio con questa agenzia' : ''}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {haColloquio ? 'Colloquio Richiesto' : 'Manifesta Interesse'}
                              </Button>
                              {haColloquio && (
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  Hai già una richiesta di colloquio con questa agenzia
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <Button
                                onClick={() => !haCVInviato && handleOpenCVModal(match)}
                                disabled={haCVInviato}
                                className={`w-full rounded-xl text-sm ${
                                  haCVInviato 
                                    ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                }`}
                                title={haCVInviato ? 'Hai già inviato il CV a questa agenzia' : ''}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                {haCVInviato ? 'CV Già Inviato' : 'Invia CV'}
                              </Button>
                              {haCVInviato && (
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  Hai già inviato il tuo CV a questa agenzia
                                </div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => handleScopriDiPiuClick(match)}
                              className="w-full rounded-xl text-sm border-gray-300 hover:bg-gray-50"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Scopri di più
                            </Button>
                          </>
                        )}

                        {match.status === 'in_attesa_contatto' && (
                          <>
                            <div className="text-center p-2 bg-yellow-50 rounded-lg">
                              <div className="text-sm text-yellow-800 font-semibold">In attesa</div>
                              <div className="text-xs text-yellow-600">Ti contatteranno presto</div>
                            </div>
                            <div className="relative group">
                              <Button
                                onClick={() => !haCVInviato && handleOpenCVModal(match)}
                                disabled={haCVInviato}
                                className={`w-full rounded-xl text-sm ${
                                  haCVInviato 
                                    ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                }`}
                                title={haCVInviato ? 'Hai già inviato il CV a questa agenzia' : ''}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                {haCVInviato ? 'CV Già Inviato' : 'Invia CV'}
                              </Button>
                              {haCVInviato && (
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  Hai già inviato il tuo CV a questa agenzia
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {(match.status === 'chiamato' || match.status === 'colloquio_fissato') && (
                          <>
                            <div className="relative group">
                              <Button
                                onClick={() => !haColloquio && handleRichiestaColloquioClick(match)}
                                disabled={haColloquio}
                                className={`w-full rounded-xl text-sm ${
                                  haColloquio 
                                    ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                    : 'bg-green-500 hover:bg-green-600 text-white'
                                }`}
                                title={haColloquio ? 'Hai già una richiesta di colloquio con questa agenzia' : ''}
                              >
                                <Calendar className="w-4 h-4 mr-2" />
                                {haColloquio ? 'Colloquio Richiesto' : 'Gestisci Colloquio'}
                              </Button>
                              {haColloquio && (
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  Hai già una richiesta di colloquio con questa agenzia
                                </div>
                              )}
                            </div>
                            <div className="relative group">
                              <Button
                                onClick={() => !haCVInviato && handleOpenCVModal(match)}
                                disabled={haCVInviato}
                                className={`w-full rounded-xl text-sm ${
                                  haCVInviato 
                                    ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                }`}
                                title={haCVInviato ? 'Hai già inviato il CV a questa agenzia' : ''}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                {haCVInviato ? 'CV Già Inviato' : 'Invia CV'}
                              </Button>
                              {haCVInviato && (
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  Hai già inviato il tuo CV a questa agenzia
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {match.status === 'inserito' && (
                          <div className="text-center p-2 bg-emerald-50 rounded-lg">
                            <div className="text-sm text-emerald-800 font-semibold">🎉 Inserito!</div>
                            <div className="text-xs text-emerald-600">Congratulazioni</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })})()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Invio CV */}
      {showCVModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Invia il tuo CV</h3>
                  <p className="text-sm text-gray-600 mt-1">A {selectedMatch.agency_name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCVModal(false);
                    setCvFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Opzione 1: CV dal profilo */}
                <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
                  <div className="flex items-start gap-3">
                    <FileText className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">CV generato dal profilo</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Genera automaticamente un CV professionale dai dati del tuo profilo
                      </p>
                      <Button
                        onClick={() => handleSendCV()}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                        disabled={cvFile !== null}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Invia CV Generato
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Divisore */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">oppure</span>
                  </div>
                </div>

                {/* Opzione 2: Carica file */}
                <div className="border-2 border-emerald-200 rounded-xl p-4 bg-emerald-50">
                  <div className="flex items-start gap-3">
                    <Upload className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">Carica il tuo CV</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Formati supportati: PDF, DOC, DOCX (max 5MB)
                      </p>
                      
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        id="cv-file-input"
                      />
                      
                      {cvFile ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-emerald-300">
                            <FileText className="w-5 h-5 text-emerald-600" />
                            <span className="text-sm font-medium text-gray-700 flex-1 truncate">
                              {cvFile.name}
                            </span>
                            <button
                              onClick={() => setCvFile(null)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <Button
                            onClick={() => handleSendCV()}
                            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Invia File Caricato
                          </Button>
                        </div>
                      ) : (
                        <label
                          htmlFor="cv-file-input"
                          className="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors"
                        >
                          <Upload className="w-5 h-5 text-emerald-600" />
                          <span className="text-sm font-medium text-emerald-700">
                            Seleziona file
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgenzieCompatibili;
